import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { getFormItemLabel } from '@/components/form-fields/utils';
import { getTranslatedString } from '@/utils';
import { SEARCH_DEBOUNCE_MS, useDebouncedCallback } from '@/utils/debounce';
import { api } from '@/api';

const { Option } = Select;
const SEARCH_PAGE_SIZE = 30;

/** Lot options for dropdown (id + number). */
interface LotOption {
  value: number;
  label: string;
}

/**
 * Select that loads lots from API on search (remote search).
 * Use when there can be many lots; avoids loading all and respects backend pagination/filtering.
 */
export const LotSearchSelect = observer(
  ({
    name,
    rules,
    i18nLabel,
    readOnly,
    ...rest
  }: {
    name: string;
    rules?: any[];
    i18nLabel: string;
    readOnly?: boolean;
  }) => {
    const [options, setOptions] = useState<LotOption[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchLots = useCallback((searchText: string) => {
      setLoading(true);
      api.lots
        .getAll({
          number: searchText || undefined,
          limit: SEARCH_PAGE_SIZE,
          skip: 0,
        })
        .source.then(({ data }: { data: { items: cyrian.api.lots.Lot[] } }) => {
          setOptions(
            (data?.items ?? []).map((lot) => ({
              value: lot.id,
              label: lot.number ?? String(lot.id),
            })),
          );
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
      fetchLots('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedFetch = useDebouncedCallback(fetchLots, SEARCH_DEBOUNCE_MS);

    const onSearch = useCallback(
      (value: string) => {
        if (value === undefined || value === null) {
          setOptions([]);
          return;
        }
        const trimmed = String(value).trim();
        debouncedFetch(trimmed);
      },
      [debouncedFetch],
    );

    const label = getFormItemLabel(i18nLabel, undefined);

    return (
      <Form.Item name={name} label={label} rules={rules}>
        <Select
          showSearch
          allowClear
          placeholder={getTranslatedString('lots.number')}
          filterOption={false}
          onSearch={onSearch}
          onFocus={() => options.length === 0 && fetchLots('')}
          loading={loading}
          disabled={readOnly}
          {...rest}
        >
          {options.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
    );
  },
);
